import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SettingsService } from './settings.service';
import { WebApiModel } from '../models/web-api.model';

@Injectable()
export class LocationService {
  constructor(private http: HttpClient) {}

  getLocations(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
    serviceCategory: string,
    service: string,
    specialty: string,
    searchPeople: boolean,
  ): Observable<WebApiModel.Location[]> {
    const params = this.buildSearchParams(
      name,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      latitude,
      longitude,
      distance,
      distanceKms,
      serviceCategory,
      service,
      specialty,
      searchPeople,
    );

    let serviceUrl = SettingsService.api.searchLocationsByNameUrl;

    if (location) {
      serviceUrl = SettingsService.api.searchLocationsByNameAndLocationUrl;
    } else if (latitude && longitude) {
      serviceUrl = SettingsService.api.searchLocationsByNameAndGeolocationUrl;
    }
    if (!distance) {
      serviceUrl = SettingsService.api.searchLocationsByNameAndGeolocationUrl;
    }

    return this.http.get(serviceUrl, { params }).pipe(
      map((response) => response as WebApiModel.Location[]), // revisit after angular 7 upgrade
      catchError((error) => throwError(error)),
    );
  }

  getLocationsByService(
    name: string,
    serviceCategory: string,
    service: string,
    specialty: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
    searchPeople: boolean,
  ): Observable<WebApiModel.Location[]> {
    const params = this.buildServiceSearchParams(
      name,
      serviceCategory,
      service,
      specialty,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      latitude,
      longitude,
      distance,
      distanceKms,
      searchPeople,
    );

    let serviceUrl = SettingsService.api.searchLocationsByServiceUrl;

    if (location) {
      serviceUrl = SettingsService.api.searchLocationsByServiceAndLocationUrl;
    } else if (latitude && longitude) {
      serviceUrl = SettingsService.api.searchLocationsByServiceAndGeolocationUrl;
    }
    if (!distance) {
      serviceUrl = SettingsService.api.searchLocationsByServiceAndGeolocationUrl;
    }

    return this.http.get(serviceUrl, { params }).pipe(
      map((response) => response as WebApiModel.Location[]), // revisit after angular 7 upgrade
      catchError((error) => throwError(error)),
    );
  }

  buildSearchParams(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
    serviceCategory: string,
    service: string,
    specialty: string,
    searchPeople: boolean,
  ): HttpParams {
    let params: HttpParams = new HttpParams();
    params = !name ? params : params.append('name', name);
    params = !location ? params : params.append('location', location);
    params = !serviceCategory ? params : params.append('serviceCategory', this.stripWildCards(serviceCategory));
    params = !service ? params : params.append('service', this.stripWildCards(service));
    params = !specialty ? params : params.append('specialty', this.stripWildCards(specialty));
    params = !searchPeople ? params : params.append('searchPeople', searchPeople.toString());
    params = !distance ? params : params.append('distanceKms', distanceKms.toString());
    params = !latitude ? params : params.append('latitude', latitude.toString());
    params = !longitude ? params : params.append('longitude', longitude.toString());

    if (affiliatedProvider === true) {
      params = params.append('isAffiliatedProvider', affiliatedProvider.toString());
    }
    params = params.append('sort', sort);
    params = params.append('isAffiliatedProviderConsult', affiliatedProviderConsult.toString());
    if (telehealth === true) {
      params = params.append('isTelehealth', telehealth.toString());
    }
    if (easyClaim === true) {
      params = params.append('isEasyClaim', easyClaim.toString());
    }

    return params;
  }

  buildServiceSearchParams(
    name: string,
    serviceCategory: string,
    service: string,
    specialty: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
    searchPeople: boolean,
  ): HttpParams {
    let params: HttpParams = new HttpParams();
    params = !name ? params : params.append('name', name);
    params = !service ? params : params.append('service', service);
    params = !specialty ? params : params.append('specialty', specialty);
    params = !serviceCategory ? params : params.append('serviceCategory', serviceCategory);
    params = !searchPeople ? params : params.append('searchPeople', searchPeople.toString());
    params = !location ? params : params.append('location', location);
    params = !distance ? params : params.append('distanceKms', distanceKms.toString());
    params = !latitude ? params : params.append('latitude', latitude.toString());
    params = !longitude ? params : params.append('longitude', longitude.toString());

    if (affiliatedProvider === true) {
      params = params.append('isAffiliatedProvider', affiliatedProvider.toString());
    }
    params = params.append('sort', sort);
    params = params.append('isAffiliatedProviderConsult', affiliatedProviderConsult.toString());
    if (telehealth === true) {
      params = params.append('isTelehealth', telehealth.toString());
    }
    if (easyClaim === true) {
      params = params.append('isEasyClaim', easyClaim.toString());
    }

    return params;
  }

  stripWildCards(term: string): string {
    if (term) {
      return term.split('%').join('');
    }
    return term;
  }
}
